import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardActions from "@material-ui/core/CardActions";
import CardMedia from "@material-ui/core/CardMedia";
import CardHeader from "@material-ui/core/CardHeader";
import Button from "@material-ui/core/Button";
import moment from "moment";
import momentTz from "moment-timezone/builds/moment-timezone-with-data";

import { useSpring, animated as a } from "react-spring";
import { StyledInput, StyledButton } from "../Typeset";
import ConfirmRefundDialog from "../ConfirmRefundDialog";
import TransferMenu from "./components/TransferMenu";
import { AlertBox } from "../alert-box";
import InfoIcon from "@material-ui/icons/Info";
import SellTicketPopup from "./components/SellTicketPopup";
import { api } from "../../api";
import TransferUrlPopup from "./components/TransferUrlPopup";
import { Chip } from "@material-ui/core";

export const useStyles = makeStyles({
  card: {
    backgroundColor: "#4a5557",
    color: "white",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 8,
  },
  buttonStyle: {
    color: "white",
  },
  buttonStylePrimary: {
    background: '#F62459',
    color: '#FFF',
    padding: "6px 16px",
    '&:hover': {
      background: '#C93756'
    },
  },
  buttonStyleFilled: {
    background: "#F62459",
    color: "#FFF",
    padding: "8px 20px",
    marginTop: 20,
    "&:hover": {
      background: "#C93756",
    },
  },
  title: {
    fontSize: "1rem",
  },
  subtitle: {
    fontSize: "0.8rem",
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    "& > *": {
      margin: "6px 6px 0 0",
    },
  },
});

export default function TicketCard({
  hash,
  status,
  currency,
  ticket_tier: ticketTier,
  reference_id: referenceId,
  event_thumbnail_url: thumbnailUrl,
  event_title: eventTitle,
  event_start_at: startDateTime,
  event_end_at: endDateTime,
  name_change_end: nameChangeEnd,
  has_name_change: hasNameChange,
  first_name: firstName,
  last_name: lastName,
  actual_price: actualPrice,
  actual_booking_fee: actualBookingFee,
  is_refund_request_enabled: isRefundRequestEnabled,
  is_resale_enabled: isResaleEnabled,
  is_transfer_fee_paid: isTransferFeePaid,
  is_transfer_enabled: isTransferEnabled,
  ticket_transfer_uri: ticketTransferUri,
  show_in_marketplace,
  ticket_transfer_price,
  max_transfer_price,
  refundrequest,
  onDownload,
  onConfirmClick,
  onRequestRefund,
  onConfirmTransferPaid,
  onConfirmTransferToggle,
  toggleLoadingModal,
}) {
  const classes = useStyles();
  const [flipped, setFlipped] = useState(false);
  const [openTransferUrlPopup, setOpenTransferUrlPopup] = useState(false);
  const [newFirstName, setNewFirstName] = useState(firstName);
  const [newLastName, setNewLastName] = useState(lastName);
  const [openConfirmRefundDialog, setOpenConfirmRefundDialog] =
    React.useState(false);
  const [openSellTicketDialog, setOpenSellTicketDialog] = React.useState(false);

  const { transform, opacity } = useSpring({
    opacity: flipped ? 1 : 0,
    transform: `perspective(600px) rotateX(${flipped ? 180 : 0}deg)`,
    config: { mass: 5, tension: 500, friction: 80 },
  });

  const tz = moment.tz.guess();
  const startStr = moment(startDateTime).format("D MMM YYYY [at] HH:mm");
  const endStr = moment(endDateTime).format("D MMM YYYY [at] HH:mm");
  const nameChangeEndStr = momentTz
    .tz(nameChangeEnd, tz)
    .format("D MMM YYYY [at] HH:mm z");
  const canChangeName = hasNameChange && moment(nameChangeEnd) > moment();

  const handleSuccess = async (payload) => {
    toggleLoadingModal(true);
    try {
      const response = await api.post(
        "triniq_ticket/v1/handle_transfer_ticket/",
        {
          paymentId: payload.paymentId,
          ticket_transfer_price: payload.ticketTransferPrice,
          show_in_marketplace: payload.showInMarketplace,
        }
      );
      setOpenSellTicketDialog(false);
      setOpenTransferUrlPopup(true);
      onConfirmTransferPaid(response.data);
    } catch (e) {
      setOpenSellTicketDialog(false);
      alert(
        e.response.data.error ||
          "There was an error processing your request. Please contact us at support@triniq.com"
      );
    }
    toggleLoadingModal(false);
  };

  const handleSubmitUpdate = (newTicketData) => {
    setOpenSellTicketDialog(false);
    onConfirmTransferToggle(newTicketData);
  };

  const handleToggleTransfer = async (payload) => {
    toggleLoadingModal(true);
    try {
      const response = await api.post(`triniq_ticket/v1/toggle_transfer/`, {
        hash: payload.hash,
        is_transfer_enabled: payload.is_transfer_enabled,
      });

      if (payload.is_transfer_enabled) {
        setOpenTransferUrlPopup(true);
      }

      onConfirmTransferToggle(response.data);
    } catch (e) {
      alert(
        e.response.data.error ||
          "There was an error processing your request. Please contact us at support@triniq.com"
      );
    }
    toggleLoadingModal(false);
  };

  const RefundMessage = refundrequest && (
    <div
      style={{
        position: "absolute",
        top: "0",
        right: "0",
        left: "0",
        fontSize: "0.875rem",
        backgroundColor: "#42474c",
        padding: "16px",
        display: "flex",
        alignItems: "center",
      }}
    >
      <InfoIcon style={{ fontSize: 20, marginRight: 10 }} />
      Refund requested {moment(refundrequest.created).format("D MMM YYYY")}
    </div>
  );

  const TransferMessage = isTransferEnabled && (
    <div
      style={{
        position: "absolute",
        top: "0",
        right: "0",
        left: "0",
        fontSize: "0.875rem",
        backgroundColor: "#42474c",
        padding: "16px",
        display: "flex",
        alignItems: "center",
      }}
    >
      <InfoIcon style={{ fontSize: 20, marginRight: 10 }} />
      Ticket listed for {currency}
      {parseFloat(ticket_transfer_price) + parseFloat(actualBookingFee)}
    </div>
  );

  const RefundSection = isRefundRequestEnabled ? (
    <div
      style={{
        position: "absolute",
        top: 0,
        width: "100%",
        textAlign: "center",
      }}
    >
      <Button
        size="small"
        className={classes.buttonStyleFilled}
        onClick={() => {
          setOpenConfirmRefundDialog(true);
        }}
      >
        REQUEST REFUND
      </Button>
    </div>
  ) : null;

  const isNameChangeOrResaleEnabled = hasNameChange || isResaleEnabled;

  return (
    <div
      style={{
        height: "100%",
        position: "relative",
      }}
    >
      <a.div
        style={{
          height: "100%",
          pointerEvents: flipped ? "none" : "auto",
          opacity: opacity.interpolate((o) => 1 - o),
          transform,
        }}
      >
        <Card className={classes.card}>
          <div
            style={{
              width: "100%",
              position: "relative",
            }}
          >
            <SellTicketPopup
              isOpen={openSellTicketDialog}
              handleClose={() => setOpenSellTicketDialog(false)}
              handleSubmit={handleSuccess}
              handleSubmitUpdate={handleSubmitUpdate}
              {...{
                firstName,
                lastName,
                ticketTier,
                eventTitle,
                hash,
                referenceId,
                actualPrice,
                actualBookingFee,
                currency,
                ticket_transfer_price,
                isTransferFeePaid,
                max_transfer_price,
                show_in_marketplace,
              }}
            />
            <ConfirmRefundDialog
              isOpen={openConfirmRefundDialog}
              handleClose={() => setOpenConfirmRefundDialog(false)}
              handleSubmit={(data) =>
                onRequestRefund({
                  ...data,
                  hash,
                  reference_id: referenceId,
                }) && setOpenConfirmRefundDialog(false)
              }
              {...{
                firstName,
                lastName,
                ticketTier,
                eventTitle,
                hash,
                referenceId,
              }}
            />
            <TransferUrlPopup
              isOpen={openTransferUrlPopup}
              handleClose={() => setOpenTransferUrlPopup(false)}
              refundAmount={`${currency}${ticket_transfer_price}`}
              ticketTransferUri={ticketTransferUri}
            />
            {refundrequest ? RefundMessage : RefundSection}
            {isTransferEnabled ? TransferMessage : null}
            <CardMedia src={thumbnailUrl} component={"img"} />
            <div
              style={{
                boxSizing: "border-box",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "inline-block",
                position: "absolute",
                bottom: "0",
                width: "100%",
                padding: 16,
                color: "rgba(255, 255, 255, 0.85)",
              }}
            >
              {eventTitle}
              <div className={classes.subtitle}>
                <span style={{ whiteSpace: "nowrap" }}>
                  <span style={{ width: 50, display: "inline-block" }}>
                    Start:
                  </span>
                  {startStr}
                </span>
                <br />
                <span style={{ whiteSpace: "nowrap" }}>
                  <span style={{ width: 50, display: "inline-block" }}>
                    End:
                  </span>
                  {endStr}
                </span>
              </div>
            </div>
          </div>
          <CardHeader
            title={
              <div>
                <div style={{ fontSize: "0.8rem" }}>Name on ticket:</div>
                {firstName} {lastName}
                <div style={{ display: "flex", width: "100%", marginTop: 6 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "0.8rem" }}>Ticket tier</div>
                    {ticketTier}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "0.8rem" }}>Ref #</div>
                    {referenceId}
                  </div>
                </div>
                {isNameChangeOrResaleEnabled && (
                  <div>
                    <div style={{ fontSize: "0.8rem", marginTop: 6 }}>
                      Name change/transfer end:
                    </div>
                    {nameChangeEndStr}
                  </div>
                )}
                <div className={classes.chipContainer}>
                  {isTransferEnabled && (
                    <Chip size="small" label="Transfer Enabled" />
                  )}
                  {isTransferEnabled && show_in_marketplace && (
                    <Chip size="small" label="Visible in Marketplace" />
                  )}
                </div>
              </div>
            }
            titleTypographyProps={{
              className: classes.title,
            }}
          />
          <div style={{ flex: 1 }} />
          {(isNameChangeOrResaleEnabled && moment(nameChangeEnd) < moment()) && (
            <AlertBox style={{
              marginTop: "10px",
              marginLeft: "8px",
              marginRight: "8px",
            }}>
              Name changes and resales for this event have ended, please contact the event organiser for more assistance.
            </AlertBox>
          )}
          {status === "VALID" && (
            <CardActions>
              {isTransferEnabled && ticketTransferUri ? (
                <Button
                  size="small"
                  className={classes.buttonStylePrimary}
                  onClick={() => setOpenTransferUrlPopup(true)}
                >
                  Transfer URL
                </Button>
              ) : (
                <Button
                  size="small"
                  className={classes.buttonStylePrimary}
                  onClick={() =>
                    onDownload({ hash, reference_id: referenceId })
                  }
                >
                  Download
                </Button>
              )}
              <div style={{ flex: 1 }} />
              <TransferMenu
                canChangeName={canChangeName}
                isResaleEnabled={isResaleEnabled}
                isTransferFeePaid={isTransferFeePaid}
                isTransferEnabled={isTransferEnabled}
                onEnableTransfer={() => {
                  handleToggleTransfer({ hash, is_transfer_enabled: true });
                }}
                onDisableTransfer={() => {
                  handleToggleTransfer({ hash, is_transfer_enabled: false });
                }}
                onChangeNameClick={() => {
                  if (momentTz.tz(nameChangeEnd, tz) >= moment()) {
                    setFlipped(true);
                  } else {
                    alert("Name changes have closed");
                  }
                }}
                onSellTicketClick={() => {
                  if (momentTz.tz(nameChangeEnd, tz) >= moment()) {
                    setOpenSellTicketDialog(true);
                  } else {
                    alert("Ticket resales have closed");
                  }
                }}
              />
            </CardActions>
          )}
        </Card>
      </a.div>
      <a.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: !flipped ? "none" : "auto",
          opacity,
          transform: transform.interpolate((t) => `${t} rotateX(180deg)`),
        }}
      >
        <Card className={classes.card}>
          <div
            style={{
              boxSizing: "border-box",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              padding: 16,
              color: "rgba(255, 255, 255, 0.85)",
            }}
          >
            {eventTitle}
            <div className={classes.subtitle}>
              <span style={{ whiteSpace: "nowrap" }}>
                <span style={{ width: 50, display: "inline-block" }}>
                  Start:
                </span>
                {startStr}
              </span>
              <br />
              <span style={{ whiteSpace: "nowrap" }}>
                <span style={{ width: 50, display: "inline-block" }}>End:</span>
                {endStr}
              </span>
            </div>
          </div>
          <div style={{ flex: 1, padding: "16px" }}>
            <div style={{ fontSize: "0.8rem", marginBottom: 6 }}>
              Update name:
            </div>
            <div>
              <StyledInput
                type="text"
                value={newFirstName}
                placeholder={firstName}
                onChange={(e) => setNewFirstName(e.target.value)}
              />
            </div>
            <div>
              <StyledInput
                type="text"
                placeholder={lastName}
                value={newLastName}
                onChange={(e) => setNewLastName(e.target.value)}
              />
            </div>
          </div>
          <CardActions>
            <StyledButton
              disabled={
                (newFirstName === firstName && newLastName === lastName) ||
                !newFirstName ||
                !newLastName
              }
              onClick={() => {
                onConfirmClick({
                  hash,
                  newFirstName,
                  newLastName,
                });
              }}
            >
              Confirm
            </StyledButton>
            <div style={{ flex: 1 }} />
            <Button
              size="small"
              className={classes.buttonStyle}
              onClick={() => setFlipped(false)}
            >
              Cancel
            </Button>
          </CardActions>
        </Card>
      </a.div>
    </div>
  );
}
